import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  E164Number,
  ISchedule,
  ISettingsValidationErrors,
  PaymentProvider,
  PaymentStrategy,
} from "../types/agent-settings";
import {
  defaultAgentSettings,
  IAgentSettings,
  emptySchedule,
} from "../types/agent-settings";
import { useSettingsService } from "../services/api/settings-service";
import { useCallersService } from "../services/api/callers-service";
import { usePostHog } from "posthog-js/react";
import { useToast } from "./ToastContext";
import { debounce } from "lodash";
import {
  getChangedSettings,
  hasSettingsChanged,
} from "../utils/settings-comparison";
import {
  validateFaqs,
  validateGreeting,
  validateOrderingHours,
  validatePhoneNumber,
  validateSchedule,
  validationErrorsDefaultPrepTime,
  validatePrepTimeScheduleFields,
  validateScheduleHasNoOverlappingSlots,
} from "../utils/setting-helpers";
import { ISO8601Duration } from "../types/order-settings";
import { IPrepTimeSchedule } from "../types/agent-settings";
import { IFAQ } from "../types/faq";
import { useFormattedDate } from "../hooks/useFormattedDate";
import { StripeAccountStatus } from "../types/stripe-account-status";
import { DEFAULT_PAYMENT_THRESHOLD } from "../constants/payments";

export function SettingsDataProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { showSuccessToast, showErrorToast } = useToast();
  const posthog = usePostHog();

  const [settings, setSettings] =
    useState<IAgentSettings>(defaultAgentSettings);
  const [blockedCallers, setBlockedCallers] = useState<E164Number[]>([]);

  const [originalSettings, setOriginalSettings] =
    useState<IAgentSettings>(defaultAgentSettings);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [validationErrors, setValidationErrors] =
    useState<ISettingsValidationErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [shouldBlurFaqs, setShouldBlurFaqs] = useState(false);
  const [isTestCallActive, setIsTestCallActive] = useState(false);
  const { formatDateTimeLong } = useFormattedDate();

  const settingsService = useSettingsService();
  const callersService = useCallersService();

  const isTypeOrderingAgent = useCallback(
    (type: string) =>
      type.includes("PICKUP") ||
      type.includes("DELIVERY") ||
      type.includes("CATERING"),
    []
  );

  const isOrderingAgent = useMemo(
    () => settings.orderingConfig != null && isTypeOrderingAgent(settings.type),
    [settings.orderingConfig, settings.type]
  );

  const stripeAccountStatus = useMemo((): StripeAccountStatus => {
    if (!settings.stripeAccountId) {
      return "NONE";
    }
    if (settings.stripeChargesEnabled === true) {
      return "READY";
    }
    return "ONBOARDING";
  }, [settings.stripeAccountId, settings.stripeChargesEnabled]);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const [settingsData, blockedCallersData] = await Promise.all([
        settingsService.fetchSettings(),
        callersService.fetchBlockedCallers(),
      ]);
      setBlockedCallers(blockedCallersData);
      setSettings(settingsData);

      setOriginalSettings(settingsData);
      setHasUnsavedChanges(false);
    } catch (error: any) {
      setError(error.message);
      showErrorToast("We're having trouble loading your settings.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveFallbackPhoneNumber = useCallback(
    (value: E164Number | null) => {
      setSettings((prev) => ({
        ...prev,
        fallbackPhoneNumber: value,
      }));

      if (validationErrors.fallbackPhoneNumber) {
        const newErrors = { ...validationErrors };
        delete newErrors.fallbackPhoneNumber;
        setValidationErrors(newErrors);
      }
    },
    []
  );

  const handleAllowAnonymousCalls = useCallback((value: boolean) => {
    posthog.capture("allow_anonymous_calls_changed", {
      allow_anonymous_calls: value,
    });
    setSettings((prev) => ({
      ...prev,
      allowAnonymousCalls: value,
    }));
  }, []);

  const handleSaveStoreHours = useCallback((schedule: ISchedule) => {
    setSettings((prev) => ({
      ...prev,
      schedule,
    }));
  }, []);

  const handleSaveOrderingHours = useCallback(
    (orderingHours: ISchedule | null) => {
      setSettings((prev) => ({
        ...prev,
        orderingHours: orderingHours || null,
      }));
    },
    []
  );

  const handleSaveGreetingMessageOpen = useCallback(
    (greeting: string) => {
      setSettings((prev) => ({
        ...prev,
        greetingMessageOpen: greeting,
      }));

      if (validationErrors.greetingMessageOpen) {
        const newErrors = { ...validationErrors };
        delete newErrors.greetingMessageOpen;
        setValidationErrors(newErrors);
      }
    },
    [validationErrors]
  );

  const handleSaveGreetingMessageClosed = useCallback(
    (greeting: string) => {
      setSettings((prev) => ({
        ...prev,
        greetingMessageClosed: greeting,
      }));

      if (validationErrors.greetingMessageClosed) {
        const newErrors = { ...validationErrors };
        delete newErrors.greetingMessageClosed;
        setValidationErrors(newErrors);
      }
    },
    [validationErrors]
  );

  const handleSaveTemporaryClosure = useCallback(
    async (timestamp: string | null, greeting: string) => {
      try {
        const partialUpdate = {
          tempClosedUntil: timestamp || null,
          greetingMessageTempClosed: greeting,
        } as const;

        const { greetingMessageTempClosed, tempClosedUntil } =
          await settingsService.updateSettings(partialUpdate);
        setSettings((settings) => ({
          ...settings,
          greetingMessageTempClosed,
          tempClosedUntil,
        }));
        setOriginalSettings((settings) => ({
          ...settings,
          greetingMessageTempClosed,
          tempClosedUntil,
        }));
        showSuccessToast(
          tempClosedUntil
            ? `Store closed until ${formatDateTimeLong(tempClosedUntil)}`
            : "Store reopened successfully"
        );
      } catch (e) {
        showErrorToast("Failed to update temporary closure");
      }
    },
    [settingsService]
  );

  const handleVoice = useCallback((newVoiceId: string) => {
    posthog.capture("voice_selected", {
      voice_id: newVoiceId,
    });
    setSettings((prev) => ({
      ...prev,
      voiceId: newVoiceId,
    }));
  }, []);

  const handleVoiceSpeed = useCallback((newVoiceSpeed: number) => {
    setSettings((prev) => ({
      ...prev,
      voiceSpeed: newVoiceSpeed,
    }));
  }, []);

  const handleAmbientNoise = useCallback((newAmbientNoise: boolean) => {
    posthog.capture("ambient_noise_selected", {
      ambient_noise: newAmbientNoise,
    });
    setSettings((prev) => ({
      ...prev,
      ambientNoise: newAmbientNoise,
    }));
  }, []);

  const handleAmbientNoiseVolume = useCallback(
    (newAmbientNoiseVolume: number) => {
      setSettings((prev) => ({
        ...prev,
        ambientNoiseVolume: newAmbientNoiseVolume,
      }));
    },
    []
  );

  const handlePrepTimeChange = useCallback(
    (prepTime: ISO8601Duration) => {
      if (prepTime.replace(/[^0-9]/g, "").length !== prepTime.length) return;

      if (validationErrors.prepTime) {
        const newErrors = { ...validationErrors };
        delete newErrors.prepTime;
        setValidationErrors(newErrors);
      }

      setSettings((prev) => ({
        ...prev,
        orderingConfig: prev.orderingConfig
          ? {
              ...prev.orderingConfig,
              prepTime: prepTime,
            }
          : null,
      }));
    },
    [validationErrors]
  );

  const handleDeliveryPrepTimeChange = useCallback((minutes: string) => {
    // accept empty string to clear
    if (
      minutes !== "" &&
      minutes.replace(/[^0-9]/g, "").length !== minutes.length
    )
      return;

    if (validationErrors.deliveryPrepTime) {
      const newErrors = { ...validationErrors };
      delete newErrors.deliveryPrepTime;
      setValidationErrors(newErrors);
    }

    setSettings((prev) => ({
      ...prev,
      deliveryPrepTime: minutes,
    }));
  }, []);

  const handlePickupPrepTimeChange = useCallback(
    (minutes: string) => {
      // Allow empty string (clearing). Enforce numeric-only characters otherwise.
      if (
        minutes !== "" &&
        minutes.replace(/[^0-9]/g, "").length !== minutes.length
      ) {
        return;
      }

      if (validationErrors.pickupPrepTime) {
        const newErrors = { ...validationErrors };
        delete newErrors.pickupPrepTime;
        setValidationErrors(newErrors);
      }

      setSettings((prev) => ({
        ...prev,
        pickupPrepTime: minutes,
      }));
    },
    [validationErrors]
  );

  const handleCustomPrepTimeEnabled = useCallback((enabled: boolean) => {
    if (validationErrors.customPrepTime) {
      const newErrors = { ...validationErrors };
      delete newErrors.customPrepTime;
      setValidationErrors(newErrors);
    }

    setSettings((prev) => ({
      ...prev,
      customPrepTimeEnabled: enabled,
    }));
  }, []);

  const handleCustomPrepTimeHours = useCallback(
    (schedule: IPrepTimeSchedule) => {
      if (validationErrors.customPrepTime) {
        const newErrors = { ...validationErrors };
        delete newErrors.customPrepTime;
        setValidationErrors(newErrors);
      }

      posthog.capture("prep_time_custom_hours_edited");

      setSettings((prev) => ({
        ...prev,
        customPrepTimeHours: schedule,
      }));
    },
    []
  );

  const handleAllowScheduledOrdersToggle = useCallback((value: boolean) => {
    if (value) {
      handleAllowScheduledOrders("normal");
    } else {
      handleAllowScheduledOrders("never");
    }
  }, []);

  const handlePickupPaymentsToggle = useCallback((value: boolean) => {
    setSettings((prev) => ({
      ...prev,
      orderingConfig: prev.orderingConfig
        ? {
            ...prev.orderingConfig,
            pickupModuleConfig: prev.orderingConfig.pickupModuleConfig
              ? {
                  ...prev.orderingConfig.pickupModuleConfig,
                  paymentModuleConfig: value
                    ? {
                        provider: PaymentProvider.STRIPE,
                        strategy: PaymentStrategy.REQUIRED,
                        requireTip: true,
                      }
                    : {
                        provider: PaymentProvider.NONE,
                      },
                }
              : null,
          }
        : null,
    }));
  }, []);

  const handleDeliveryPaymentsToggle = useCallback((value: boolean) => {
    setSettings((prev) => ({
      ...prev,
      orderingConfig: prev.orderingConfig
        ? {
            ...prev.orderingConfig,
            deliveryModuleConfig: prev.orderingConfig.deliveryModuleConfig
              ? {
                  ...prev.orderingConfig.deliveryModuleConfig,
                  paymentModuleConfig: value
                    ? {
                        provider: PaymentProvider.STRIPE,
                        strategy: PaymentStrategy.REQUIRED,
                        requireTip: true,
                      }
                    : {
                        provider: PaymentProvider.NONE,
                      },
                }
              : null,
          }
        : null,
    }));
  }, []);

  const handlePickupTipPromptToggle = useCallback((value: boolean) => {
    setSettings((prev) => ({
      ...prev,
      orderingConfig: prev.orderingConfig
        ? {
            ...prev.orderingConfig,
            pickupModuleConfig: prev.orderingConfig.pickupModuleConfig
              ? {
                  ...prev.orderingConfig.pickupModuleConfig,
                  paymentModuleConfig:
                    prev.orderingConfig.pickupModuleConfig
                      .paymentModuleConfig &&
                    prev.orderingConfig.pickupModuleConfig.paymentModuleConfig
                      .provider === PaymentProvider.STRIPE
                      ? {
                          ...prev.orderingConfig.pickupModuleConfig
                            .paymentModuleConfig,
                          requireTip: value,
                        }
                      : prev.orderingConfig.pickupModuleConfig
                          .paymentModuleConfig,
                }
              : null,
          }
        : null,
    }));
  }, []);

  const handlePickupPaymentThresholdChange = useCallback(
    (threshold: number) => {
      setSettings((prev) => ({
        ...prev,
        orderingConfig: prev.orderingConfig
          ? {
              ...prev.orderingConfig,
              pickupModuleConfig: prev.orderingConfig.pickupModuleConfig
                ? {
                    ...prev.orderingConfig.pickupModuleConfig,
                    paymentModuleConfig:
                      prev.orderingConfig.pickupModuleConfig.paymentModuleConfig
                        .provider === PaymentProvider.STRIPE
                        ? {
                            provider: PaymentProvider.STRIPE,
                            strategy: PaymentStrategy.REQUIRED_ABOVE,
                            requiredAboveThreshold: threshold,
                            requireTip:
                              "requireTip" in
                              prev.orderingConfig.pickupModuleConfig
                                .paymentModuleConfig
                                ? prev.orderingConfig.pickupModuleConfig
                                    .paymentModuleConfig.requireTip
                                : true,
                          }
                        : prev.orderingConfig.pickupModuleConfig
                            .paymentModuleConfig,
                  }
                : null,
            }
          : null,
      }));
    },
    []
  );

  const handleDeliveryPaymentThresholdChange = useCallback(
    (threshold: number) => {
      setSettings((prev) => ({
        ...prev,
        orderingConfig: prev.orderingConfig
          ? {
              ...prev.orderingConfig,
              deliveryModuleConfig: prev.orderingConfig.deliveryModuleConfig
                ? {
                    ...prev.orderingConfig.deliveryModuleConfig,
                    paymentModuleConfig:
                      prev.orderingConfig.deliveryModuleConfig
                        .paymentModuleConfig.provider === PaymentProvider.STRIPE
                        ? {
                            provider: PaymentProvider.STRIPE,
                            strategy: PaymentStrategy.REQUIRED_ABOVE,
                            requiredAboveThreshold: threshold,
                            requireTip:
                              "requireTip" in
                              prev.orderingConfig.deliveryModuleConfig
                                .paymentModuleConfig
                                ? prev.orderingConfig.deliveryModuleConfig
                                    .paymentModuleConfig.requireTip
                                : true,
                          }
                        : prev.orderingConfig.deliveryModuleConfig
                            .paymentModuleConfig,
                  }
                : null,
            }
          : null,
      }));
    },
    []
  );

  const handlePickupPaymentThresholdToggle = useCallback((enabled: boolean) => {
    setSettings((prev) => ({
      ...prev,
      orderingConfig: prev.orderingConfig
        ? {
            ...prev.orderingConfig,
            pickupModuleConfig: prev.orderingConfig.pickupModuleConfig
              ? {
                  ...prev.orderingConfig.pickupModuleConfig,
                  paymentModuleConfig:
                    prev.orderingConfig.pickupModuleConfig.paymentModuleConfig
                      .provider === PaymentProvider.STRIPE
                      ? enabled
                        ? {
                            provider: PaymentProvider.STRIPE,
                            strategy: PaymentStrategy.REQUIRED_ABOVE,
                            requiredAboveThreshold: DEFAULT_PAYMENT_THRESHOLD,
                            requireTip:
                              "requireTip" in
                              prev.orderingConfig.pickupModuleConfig
                                .paymentModuleConfig
                                ? prev.orderingConfig.pickupModuleConfig
                                    .paymentModuleConfig.requireTip
                                : true,
                          }
                        : {
                            provider: PaymentProvider.STRIPE,
                            strategy: PaymentStrategy.REQUIRED,
                            requireTip:
                              "requireTip" in
                              prev.orderingConfig.pickupModuleConfig
                                .paymentModuleConfig
                                ? prev.orderingConfig.pickupModuleConfig
                                    .paymentModuleConfig.requireTip
                                : true,
                          }
                      : prev.orderingConfig.pickupModuleConfig
                          .paymentModuleConfig,
                }
              : null,
          }
        : null,
    }));
  }, []);

  const handleDeliveryPaymentThresholdToggle = useCallback(
    (enabled: boolean) => {
      setSettings((prev) => ({
        ...prev,
        orderingConfig: prev.orderingConfig
          ? {
              ...prev.orderingConfig,
              deliveryModuleConfig: prev.orderingConfig.deliveryModuleConfig
                ? {
                    ...prev.orderingConfig.deliveryModuleConfig,
                    paymentModuleConfig:
                      prev.orderingConfig.deliveryModuleConfig
                        .paymentModuleConfig.provider === PaymentProvider.STRIPE
                        ? enabled
                          ? {
                              provider: PaymentProvider.STRIPE,
                              strategy: PaymentStrategy.REQUIRED_ABOVE,
                              requiredAboveThreshold: DEFAULT_PAYMENT_THRESHOLD,
                              requireTip:
                                "requireTip" in
                                prev.orderingConfig.deliveryModuleConfig
                                  .paymentModuleConfig
                                  ? prev.orderingConfig.deliveryModuleConfig
                                      .paymentModuleConfig.requireTip
                                  : true,
                            }
                          : {
                              provider: PaymentProvider.STRIPE,
                              strategy: PaymentStrategy.REQUIRED,
                              requireTip:
                                "requireTip" in
                                prev.orderingConfig.deliveryModuleConfig
                                  .paymentModuleConfig
                                  ? prev.orderingConfig.deliveryModuleConfig
                                      .paymentModuleConfig.requireTip
                                  : true,
                            }
                        : prev.orderingConfig.deliveryModuleConfig
                            .paymentModuleConfig,
                  }
                : null,
            }
          : null,
      }));
    },
    []
  );

  const handleAllowScheduledOrders = useCallback(
    (value: "always" | "never" | "normal" | "morning") => {
      setSettings((prev) => ({
        ...prev,
        orderingConfig: prev.orderingConfig
          ? {
              ...prev.orderingConfig,
              allowScheduledOrders: value,
            }
          : null,
      }));
    },
    []
  );

  const handleOrderUntilClosed = useCallback((value: boolean) => {
    setSettings((prev) => ({
      ...prev,
      orderingConfig: prev.orderingConfig
        ? {
            ...prev.orderingConfig,
            orderUntilClosed: value,
          }
        : null,
    }));
  }, []);

  const handleShouldTotal = useCallback((value: boolean) => {
    setSettings((prev) => ({
      ...prev,
      orderingConfig: prev.orderingConfig
        ? {
            ...prev.orderingConfig,
            shouldTotal: value,
          }
        : null,
    }));
  }, []);

  const handleExactTimeEstimate = useCallback((value: boolean) => {
    setSettings((prev) => ({
      ...prev,
      orderingConfig: prev.orderingConfig
        ? {
            ...prev.orderingConfig,
            exactTimeEstimate: value,
          }
        : null,
    }));
  }, []);

  const handleDynamicPrepTime = useCallback((value: boolean) => {
    setSettings((prev) => ({
      ...prev,
      orderingConfig: prev.orderingConfig
        ? {
            ...prev.orderingConfig,
            dynamicPrepTime: value,
          }
        : null,
    }));
  }, []);

  const handleFaq = useCallback((newFaqs: IFAQ[]) => {
    setSettings((prev) => ({
      ...prev,
      faqs: newFaqs,
    }));
  }, []);

  const handleAddBlockedCaller = useCallback(
    async (number: E164Number) => {
      posthog.capture("caller_blocked", {
        phone: number,
      });
      try {
        await callersService.addBlockedCaller(number);
        setBlockedCallers((prev) => [...prev, number]);
        showSuccessToast("Added blocked caller successfully.");
        return true;
      } catch (error) {
        showErrorToast("Error adding blocked caller.");
        return false;
      }
    },
    [setBlockedCallers, settingsService]
  );

  const handleDeleteBlockedCaller = useCallback(
    async (number: E164Number) => {
      try {
        posthog.capture("caller_unblocked", {
          phone: number,
        });
        await callersService.deleteBlockedCaller(number);
        setBlockedCallers((prev) => prev.filter((num) => num !== number));
        showSuccessToast("Unblocked caller successfully.");
        return true;
      } catch (error) {
        showErrorToast("Failed to unblock caller.");
        return false;
      }
    },
    [setBlockedCallers, settingsService]
  );

  const debouncedSetHasUnsavedChanges = useMemo(
    () =>
      debounce((current: IAgentSettings, original: IAgentSettings) => {
        setHasUnsavedChanges(hasSettingsChanged(current, original));
      }, 500),
    []
  );

  useEffect(() => {
    if (isTestCallActive) {
      setHasUnsavedChanges(false);
    } else {
      debouncedSetHasUnsavedChanges(settings, originalSettings);
    }
    return () => {
      debouncedSetHasUnsavedChanges.cancel();
    };
  }, [
    settings,
    originalSettings,
    debouncedSetHasUnsavedChanges,
    isTestCallActive,
  ]);

  const handleDiscardChanges = useCallback(() => {
    posthog.capture("settings_discarded");
    setSettings(originalSettings);
    setHasUnsavedChanges(false);
    setValidationErrors({});
    setShouldBlurFaqs(true);
    setTimeout(() => {
      setShouldBlurFaqs(false);
    }, 250);
  }, [originalSettings]);

  const validateForm = useCallback(() => {
    const newErrors: ISettingsValidationErrors = {};
    if (!validateGreeting(settings.greetingMessageOpen)) {
      newErrors.greetingMessageOpen = "Please enter a greeting message.";
    }
    if (!validateGreeting(settings.greetingMessageClosed)) {
      newErrors.greetingMessageClosed = "Please enter a greeting message.";
    }
    if (!validateSchedule(settings.schedule)) {
      newErrors.schedule = "Please enter valid times for all days.";
    }
    if (
      settings.posProvider !== "stream" &&
      (!validateSchedule(settings.orderingHours) ||
        !validateOrderingHours(settings.orderingHours, settings.schedule))
    ) {
      newErrors.orderingHours = "Please enter valid times for all days.";
    }
    if (
      settings.fallbackPhoneNumber &&
      !validatePhoneNumber(settings.fallbackPhoneNumber)
    ) {
      newErrors.fallbackPhoneNumber = "Please enter a valid phone number.";
    }

    if (
      isOrderingAgent &&
      (settings.pickupPrepTime !== originalSettings.pickupPrepTime ||
        settings.deliveryPrepTime !== originalSettings.deliveryPrepTime ||
        settings.customPrepTimeEnabled !==
          originalSettings.customPrepTimeEnabled ||
        settings.customPrepTimeHours !== originalSettings.customPrepTimeHours)
    ) {
      const isDeliveryEnabled =
        !!settings.orderingConfig?.deliveryModuleConfig?.enabled;
      const isPickupEnabled =
        !!settings.orderingConfig?.pickupModuleConfig?.enabled;

      const {
        pickupPrepTime: pickupPrepTimeErrors,
        deliveryPrepTime: deliveryPrepTimeErrors,
      } = validationErrorsDefaultPrepTime({
        pickupPrepTime: settings.pickupPrepTime,
        deliveryPrepTime: settings.deliveryPrepTime,
        pickupEnabled: isPickupEnabled,
        deliveryEnabled: isDeliveryEnabled,
      });
      if (pickupPrepTimeErrors) newErrors.pickupPrepTime = pickupPrepTimeErrors;
      if (deliveryPrepTimeErrors)
        newErrors.deliveryPrepTime = deliveryPrepTimeErrors;

      if (settings.customPrepTimeEnabled) {
        if (
          !validateSchedule(settings.customPrepTimeHours as ISchedule | null) ||
          !validateOrderingHours(
            settings.customPrepTimeHours as ISchedule | null,
            settings.schedule
          )
        ) {
          newErrors.customPrepTime = "Please ensure all time slots are valid.";
        } else if (
          !validatePrepTimeScheduleFields(settings.customPrepTimeHours || null)
        ) {
          newErrors.customPrepTime =
            "Pickup requires ≥10 minutes, and delivery time ≥ pickup time.";
        } else if (
          !validateScheduleHasNoOverlappingSlots(
            settings.customPrepTimeHours || null
          )
        ) {
          newErrors.customPrepTime =
            "Your prep time schedule has conflicts. Remove or adjust overlapping slots to continue.";
        }
      }
    }

    if (!validateFaqs(settings.faqs)) {
      newErrors.faqs = "Please enter a question and answer for each FAQ.";
    }

    if (Object.keys(newErrors).length > 0) {
      console.error("Error saving settings:", newErrors);
    }
    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [settings]);

  const handleSaveChanges = useCallback(async () => {
    setIsSaveLoading(true);
    posthog.capture("settings_saved");

    if (!validateForm()) {
      setIsSaveLoading(false);
      showErrorToast(
        "We were unable to save your settings. Please review and fix any errors."
      );
      return;
    }

    setValidationErrors({});
    try {
      const changedSettings = getChangedSettings(settings, originalSettings);
      if (Object.keys(changedSettings).length === 0) {
        setIsSaveLoading(false);
        return;
      }

      const updatedSettings =
        await settingsService.updateSettings(changedSettings);
      setSettings(updatedSettings);
      setOriginalSettings(updatedSettings);
      setHasUnsavedChanges(false);
      setShouldBlurFaqs(true);
      showSuccessToast("Settings saved successfully");
    } catch (error) {
      showErrorToast("Failed to save settings.");
    } finally {
      setIsSaveLoading(false);
      setTimeout(() => {
        setShouldBlurFaqs(false);
      }, 250);
    }
  }, [
    validateForm,
    getChangedSettings,
    settings,
    originalSettings,
    settingsService,
  ]);

  return (
    <SettingsDataContext.Provider
      value={{
        settings,
        originalSettings,
        loadSettings,
        blockedCallers,
        isTestCallActive,
        handleTestCallActive: setIsTestCallActive,
        handleSaveFallbackPhoneNumber,
        handleAllowAnonymousCalls,
        handleSaveStoreHours,
        handleSaveOrderingHours,
        handleSaveGreetingMessageOpen,
        handleSaveGreetingMessageClosed,
        handleSaveTemporaryClosure,
        handleVoice,
        handleVoiceSpeed,
        handleAmbientNoise,
        handleAmbientNoiseVolume,
        handlePrepTimeChange,
        handleDeliveryPrepTimeChange,
        handlePickupPrepTimeChange,
        handleCustomPrepTimeEnabled,
        handleCustomPrepTimeHours,
        handleAllowScheduledOrdersToggle,
        handlePickupPaymentsToggle,
        handlePickupTipPromptToggle,
        handlePickupPaymentThresholdChange,
        handlePickupPaymentThresholdToggle,
        handleDeliveryPaymentsToggle,
        handleDeliveryPaymentThresholdChange,
        handleDeliveryPaymentThresholdToggle,
        handleAllowScheduledOrders,
        handleOrderUntilClosed,
        handleShouldTotal,
        handleExactTimeEstimate,
        handleDynamicPrepTime,
        handleFaq,
        handleAddBlockedCaller,
        handleDeleteBlockedCaller,
        isLoading,
        error,
        isSaveLoading,
        hasUnsavedChanges,
        handleSaveChanges,
        handleDiscardChanges,
        validationErrors,
        isOrderingAgent,
        shouldBlurFaqs,
        setShouldBlurFaqs,
        stripeAccountStatus,
      }}
    >
      {children}
    </SettingsDataContext.Provider>
  );
}

interface SettingsDataContextType {
  settings: IAgentSettings;
  originalSettings: IAgentSettings;
  loadSettings: () => void;
  blockedCallers: E164Number[];
  isTestCallActive: boolean;
  handleTestCallActive: (isTestCallActive: boolean) => void;
  handleSaveFallbackPhoneNumber: (value: E164Number | null) => void;
  handleAllowAnonymousCalls: (value: boolean) => void;
  handleSaveStoreHours: (schedule: ISchedule) => void;
  handleSaveOrderingHours: (orderingHours: ISchedule | null) => void;
  handleSaveGreetingMessageOpen: (greeting: string) => void;
  handleSaveGreetingMessageClosed: (greeting: string) => void;
  handleSaveTemporaryClosure: (
    tempClosedUntil: string | null,
    greeting: string
  ) => void;
  handleVoice: (newVoiceId: string) => void;
  handleVoiceSpeed: (newVoiceSpeed: number) => void;
  handleAmbientNoise: (newAmbientNoise: boolean) => void;
  handleAmbientNoiseVolume: (newAmbientNoiseVolume: number) => void;
  handlePrepTimeChange: (prepTime: ISO8601Duration) => void;
  handleDeliveryPrepTimeChange: (minutes: string) => void;
  handlePickupPrepTimeChange: (minutes: string) => void;
  handleCustomPrepTimeEnabled: (enabled: boolean) => void;
  handleCustomPrepTimeHours: (schedule: IPrepTimeSchedule) => void;
  handleAllowScheduledOrdersToggle: (value: boolean) => void;
  handlePickupPaymentsToggle: (value: boolean) => void;
  handlePickupTipPromptToggle: (value: boolean) => void;
  handlePickupPaymentThresholdChange: (threshold: number) => void;
  handlePickupPaymentThresholdToggle: (enabled: boolean) => void;
  handleDeliveryPaymentsToggle: (value: boolean) => void;
  handleDeliveryPaymentThresholdChange: (threshold: number) => void;
  handleDeliveryPaymentThresholdToggle: (enabled: boolean) => void;
  handleAllowScheduledOrders: (
    value: "always" | "never" | "normal" | "morning"
  ) => void;
  handleOrderUntilClosed: (value: boolean) => void;
  handleShouldTotal: (value: boolean) => void;
  handleExactTimeEstimate: (value: boolean) => void;
  handleDynamicPrepTime: (value: boolean) => void;
  handleFaq: (newFaqs: IFAQ[]) => void;
  handleAddBlockedCaller: (number: E164Number) => Promise<boolean>;
  handleDeleteBlockedCaller: (number: E164Number) => Promise<boolean>;
  isLoading: boolean;
  error: Error | null;
  isSaveLoading: boolean;
  hasUnsavedChanges: boolean;
  handleSaveChanges: () => void;
  handleDiscardChanges: () => void;
  validationErrors: ISettingsValidationErrors;
  isOrderingAgent: boolean;
  shouldBlurFaqs: boolean;
  setShouldBlurFaqs: (value: boolean) => void;
  stripeAccountStatus: StripeAccountStatus;
}

const SettingsDataContext = createContext<SettingsDataContextType | undefined>(
  undefined
);

export function useSettingsData() {
  const context = useContext(SettingsDataContext);
  if (context === undefined) {
    throw new Error(
      "useSettingsData must be used within a SettingsDataProvider"
    );
  }
  return context;
}
